import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import EraseAllButton from "../components/EraseAllButton";
import { api } from "../lib/api";
import { Loader2, Search, ChevronRight } from "lucide-react";

const SORTS = [
  { key: "newest",   label: "Newest" },
  { key: "72h",      label: "Last 72H" },
  { key: "month",    label: "This Month" },
  { key: "lifetime", label: "Lifetime" },
];

const STAT_KEY = { "72h": "spend_72h", "month": "spend_month", "lifetime": "spend_lifetime" };

export default function MemberLookup() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("newest");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (sort && sort !== "newest") params.sort = sort;
      const r = await api.get("/admin/members", { params });
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sort]);

  const statKey = STAT_KEY[sort];
  const statLabel = SORTS.find(s => s.key === sort)?.label || "";

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 py-10">
        <button onClick={() => nav("/pos/dashboard")} className="label-eyebrow mb-6 inline-block" data-testid="ml-back">← Back</button>
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <h1 className="font-display text-4xl font-black">Members</h1>
          <EraseAllButton scope="members" label="Erase All Members" confirmText="every member account and all member transaction history" onDone={load} testid="members-erase-all" />
        </div>

        <div className="flex flex-wrap gap-3 mb-6 items-center">
          <form onSubmit={(e) => { e.preventDefault(); load(); }} className="surface flex items-stretch flex-1 min-w-[260px] max-w-md">
            <input className="flex-1 bg-transparent border-0 px-4 py-3 outline-none font-mono"
                   placeholder="Search phone / name / email…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="ml-search" />
            <button type="submit" className="px-5" style={{ background: "var(--ab-surface-2)" }} data-testid="ml-search-btn"><Search size={16} /></button>
          </form>
          <div className="flex border" style={{ borderColor: "var(--ab-border)" }} role="tablist" data-testid="ml-sort-group">
            {SORTS.map((s) => (
              <button
                key={s.key}
                onClick={() => setSort(s.key)}
                className="px-4 py-3 text-xs uppercase tracking-widest font-bold border-l first:border-l-0"
                style={{
                  background: sort === s.key ? "var(--ab-primary)" : "transparent",
                  color: sort === s.key ? "#FFFFFF" : "var(--ab-text-muted)",
                  borderColor: "var(--ab-border)",
                }}
                data-testid={`ml-sort-${s.key}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? <Loader2 className="animate-spin" /> : (
          <div className="surface divide-y" style={{ borderColor: "var(--ab-border)" }}>
            {items.length === 0 && <div className="p-6 text-sm" style={{ color: "var(--ab-text-muted)" }} data-testid="ml-empty">No members yet.</div>}
            {items.map((m, idx) => (
              <Link key={m.phone} to={`/pos/members/${m.phone}`} className="flex items-center px-5 py-4 hover:bg-[var(--ab-surface-2)] transition-colors" data-testid={`ml-row-${m.phone}`}>
                {sort !== "newest" && (
                  <span className="font-mono text-xs w-7 text-right mr-3" style={{ color: idx < 3 ? "var(--ab-primary)" : "var(--ab-text-dim)" }}>
                    {idx + 1}.
                  </span>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-bold">{m.first_name} {m.last_name}</div>
                  <div className="text-xs" style={{ color: "var(--ab-text-dim)" }}>{m.phone} · {m.email}</div>
                </div>
                <div className="text-right mr-3">
                  {sort !== "newest" && statKey ? (
                    <>
                      <div className="font-mono font-bold text-lg" style={{ color: "var(--ab-primary)" }}>
                        ${Number(m[statKey] || 0).toFixed(2)}
                      </div>
                      <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--ab-text-dim)" }}>{statLabel}</div>
                    </>
                  ) : (
                    <>
                      <div className="font-mono font-bold" style={{ color: "var(--ab-primary)" }}>
                        {Number(m.points).toLocaleString()} <span className="text-xs" style={{ color: "var(--ab-text-dim)" }}>µ</span>
                      </div>
                      <div className="text-[10px]" style={{ color: "var(--ab-text-dim)" }}>lifetime ${Number(m.spend_lifetime || 0).toFixed(2)}</div>
                    </>
                  )}
                </div>
                <ChevronRight size={16} style={{ color: "var(--ab-text-dim)" }} />
              </Link>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
